/** Card de presupuesto del cliente: barras de consumo + export Excel/PDF. */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Wallet, Pencil, FileSpreadsheet, FileText } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { BudgetDialog } from "@/components/feature/media/budget-dialog";
import { ConsumptionBar } from "@/components/feature/media/consumption-bar";
import { media } from "@/lib/api";

export function BudgetCard({ clientId }: { clientId: number }) {
  const [editOpen, setEditOpen] = useState(false);
  const budgetQ = useQuery({ queryKey: ["media", "budget", clientId], queryFn: () => media.getClientBudget(clientId) });
  const consQ = useQuery({ queryKey: ["media", "budget", "consumption", clientId], queryFn: () => media.getClientBudgetConsumption(clientId) });

  const budget = budgetQ.data ?? null;
  const cons = consQ.data;

  return (
    <Card>
      <CardContent className="py-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="font-semibold flex items-center gap-2">
            <Wallet size={16} className="text-brand-600" /> Presupuesto
          </div>
          <div className="flex items-center gap-1">
            <Button size="icon" variant="ghost" className="h-8 w-8" title="Excel" asChild>
              <a href={media.budgetExportXlsxUrl(clientId)} download><FileSpreadsheet size={15} /></a>
            </Button>
            <Button size="icon" variant="ghost" className="h-8 w-8" title="PDF" asChild>
              <a href={media.budgetExportPdfUrl(clientId)} download><FileText size={15} /></a>
            </Button>
            <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>
              <Pencil size={14} /> Editar
            </Button>
          </div>
        </div>

        {budgetQ.isLoading || consQ.isLoading ? (
          <Skeleton className="h-24" />
        ) : !cons ? (
          <p className="text-xs text-ink-400">Sin datos de presupuesto.</p>
        ) : (
          <div className="space-y-3">
            <ConsumptionBar
              label="Total"
              amount={cons.total_amount}
              spent={cons.total_spent}
              pct={cons.total_pct}
              currency={cons.currency}
            />
            {cons.segments.length > 0 && (
              <div className="space-y-2 pt-2 border-t border-ink-100 dark:border-ink-800">
                <div className="text-[10px] uppercase tracking-widest text-ink-400">Segmentos</div>
                {cons.segments.map((s, i) => (
                  <ConsumptionBar key={i} label={s.label} amount={s.amount} spent={s.spent} pct={s.pct} currency={cons.currency} />
                ))}
              </div>
            )}
            {!budget?.total_amount && cons.segments.length === 0 && (
              <p className="text-xs text-ink-400">
                Aún no hay presupuesto definido. Consumo comprometido actual:{" "}
                {cons.currency} {cons.total_spent.toLocaleString("es-PE", { minimumFractionDigits: 2 })}.
              </p>
            )}
          </div>
        )}
      </CardContent>

      <BudgetDialog clientId={clientId} open={editOpen} onOpenChange={setEditOpen} budget={budget} />
    </Card>
  );
}
